import { auth } from './firebase';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';

const EMAIL_FOR_SIGNIN_KEY = 'emailForSignIn';
/** Must match POST_LOGIN_COLLAGES_SESSION_KEY in App.js — opens Fox Chat after reload-less sign-in. */
const POST_LOGIN_COLLAGES_SESSION_KEY = 'luvwtf_postLoginCollages';

const LINK_EMAIL_PARAM = 'linkEmail';

function normalizeEmailCandidate(raw) {
  if (raw == null || typeof raw !== 'string') return null;
  const normalized = raw.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) return null;
  return normalized;
}

function linkEmailFromSearchParams(searchParams) {
  if (!searchParams) return null;
  return normalizeEmailCandidate(searchParams.get(LINK_EMAIL_PARAM));
}

/**
 * Firebase email-link handler URLs put our app URL (with ?linkEmail=…) inside an encoded
 * `continueUrl` / `url` query param. Walk decodes and parse nested URLs until we find linkEmail.
 */
function readLinkEmailFromEncodedTarget(encoded) {
  if (!encoded || typeof encoded !== 'string') return null;
  let cursor = encoded.trim();
  for (let i = 0; i < 4; i += 1) {
    try {
      const asUrl = new URL(cursor);
      const found = linkEmailFromSearchParams(asUrl.searchParams);
      if (found) return found;
    } catch {
      /* not a full URL yet */
    }
    try {
      cursor = decodeURIComponent(cursor.replace(/\+/g, '%20'));
    } catch {
      break;
    }
  }
  return null;
}

function readEmailFromSignInUrl(href) {
  try {
    const u = new URL(href);

    const direct = linkEmailFromSearchParams(u.searchParams);
    if (direct) return direct;

    for (const key of ['continueUrl', 'url']) {
      const packed = u.searchParams.get(key);
      if (!packed) continue;
      const nested = readLinkEmailFromEncodedTarget(packed);
      if (nested) return nested;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Completes Firebase email-link (passwordless) sign-in once per page load, before React mounts.
 * This avoids React Strict Mode double-mounting effects, which consumes the one-time link twice and breaks sign-in.
 * @see https://firebase.google.com/docs/auth/web/email-link-auth
 */
export async function completeEmailLinkSignInIfPresent() {
  if (typeof window === 'undefined') return;

  const href = window.location.href;
  if (!isSignInWithEmailLink(auth, href)) return;

  let email = readEmailFromSignInUrl(href);

  if (!email) {
    try {
      email = window.localStorage.getItem(EMAIL_FOR_SIGNIN_KEY);
    } catch (_) {}
  }

  if (!email) {
    email = window.prompt(
      'Please provide your email for confirmation.',
    );
  }

  const normalized =
    typeof email === 'string' ? email.trim().toLowerCase() : '';

  if (!normalized) return;

  /** Preserve deep-links after sign-in (`/?makeCollage=1`, `/?yourRewards=1`, `/?openMessages=1`). Capture before `signInWithEmailLink` mutates URL. */
  let pathAfterSignIn = '/';
  try {
    const u = new URL(window.location.href);
    const fromQuery = () => {
      if (u.searchParams.get('makeCollage') === '1') return '/?makeCollage=1';
      if (u.searchParams.get('yourRewards') === '1') return '/?yourRewards=1';
      if (u.searchParams.get('openMessages') === '1') return '/?openMessages=1';
      const rawContinue = u.searchParams.get('continueUrl');
      if (rawContinue) {
        try {
          const c = new URL(decodeURIComponent(rawContinue));
          if (c.searchParams.get('makeCollage') === '1') return '/?makeCollage=1';
          if (c.searchParams.get('yourRewards') === '1') return '/?yourRewards=1';
          if (c.searchParams.get('openMessages') === '1') return '/?openMessages=1';
        } catch (_) {}
      }
      return '/';
    };
    pathAfterSignIn = fromQuery();
  } catch (_) {}

  try {
    await signInWithEmailLink(auth, normalized, href);
  } catch (err) {
    console.error('Email link sign-in failed:', err?.code || err?.message || err);
    throw err;
  }

  try {
    window.localStorage.removeItem(EMAIL_FOR_SIGNIN_KEY);
  } catch (_) {}

  try {
    window.sessionStorage.setItem(POST_LOGIN_COLLAGES_SESSION_KEY, '1');
  } catch (_) {}

  // Always send the user home. If the email link redirected to `/set-password` (e.g. console default
  // action URL), leaving pathname as `/set-password` with query stripped makes SetPasswordPage think
  // the reset link is missing even though email-link sign-in already succeeded.
  window.history.replaceState({}, '', pathAfterSignIn);
}
